import { useEffect } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { CheckCircle, XCircle, Clock, Send, AlertCircle } from "lucide-react";
import { PageWrapper, DarkNav, GlassCard, DarkFooter } from "../components/PageShell";

const REFUND_CSS = `
  @keyframes stepReveal {
    from { opacity:0; transform:translateX(-24px); }
    to   { opacity:1; transform:translateX(0); }
  }
  @keyframes greenGlow {
    0%,100% { box-shadow:0 0 0 0 rgba(16,185,129,0); }
    50%      { box-shadow:0 0 20px rgba(16,185,129,0.15); }
  }
  .refund-step-0 { animation: stepReveal 0.5s 0.1s both; }
  .refund-step-1 { animation: stepReveal 0.5s 0.2s both; }
  .refund-step-2 { animation: stepReveal 0.5s 0.3s both; }
  .refund-step-3 { animation: stepReveal 0.5s 0.4s both; }
  .refund-step-4 { animation: stepReveal 0.5s 0.5s both; }
  .refund-card { animation: greenGlow 4s ease-in-out infinite; }
`;

function P({ c, children }) {
  return <p style={{ fontSize:14.5, color: c||"rgba(255,255,255,0.6)", lineHeight:1.9, margin:"0 0 14px" }}>{children}</p>;
}
function GreenBL({ items }) {
  return (
    <ul style={{ listStyle:"none", padding:0, margin:"10px 0 16px", display:"flex", flexDirection:"column", gap:10 }}>
      {items.map((it,i) => (
        <li key={i} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
          <CheckCircle size={15} color="#10B981" style={{ flexShrink:0, marginTop:3 }} />
          <span style={{ fontSize:14, color:"rgba(255,255,255,0.68)", lineHeight:1.85 }}>{it}</span>
        </li>
      ))}
    </ul>
  );
}
function RedBL({ items }) {
  return (
    <ul style={{ listStyle:"none", padding:0, margin:"10px 0 16px", display:"flex", flexDirection:"column", gap:10 }}>
      {items.map((it,i) => (
        <li key={i} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
          <XCircle size={15} color="#F87171" style={{ flexShrink:0, marginTop:3 }} />
          <span style={{ fontSize:14, color:"rgba(255,255,255,0.62)", lineHeight:1.85 }}>{it}</span>
        </li>
      ))}
    </ul>
  );
}
function NeutralBL({ items }) {
  return (
    <ul style={{ listStyle:"none", padding:0, margin:"10px 0 16px", display:"flex", flexDirection:"column", gap:10 }}>
      {items.map((it,i) => (
        <li key={i} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
          <span style={{ marginTop:9, width:5, height:5, borderRadius:"50%", flexShrink:0, background:"linear-gradient(135deg,#10B981,#6366F1)", boxShadow:"0 0 6px rgba(16,185,129,0.5)" }} />
          <span style={{ fontSize:14, color:"rgba(255,255,255,0.62)", lineHeight:1.85 }}>{it}</span>
        </li>
      ))}
    </ul>
  );
}
function SBox({ icon, color, bg, border, children }) {
  return (
    <div style={{
      background:bg||"rgba(16,185,129,0.07)", border:`1px solid ${border||"rgba(16,185,129,0.28)"}`,
      borderLeft:`3px solid ${color||"#10B981"}`, borderRadius:"0 12px 12px 0",
      padding:"14px 18px", margin:"14px 0",
      fontSize:13.5, color:"rgba(167,243,208,0.9)", lineHeight:1.75,
      display:"flex", gap:10, alignItems:"flex-start",
    }}>
      {icon && <span style={{ fontSize:16, flexShrink:0 }}>{icon}</span>}
      <span style={{ color:"rgba(255,255,255,0.72)" }}>{children}</span>
    </div>
  );
}

export default function RefundPage() {
  const isMobile = useIsMobile();
  const px = isMobile ? 16 : 40;

  useEffect(() => {
    document.documentElement.classList.add("page-scrollable");
    return () => document.documentElement.classList.remove("page-scrollable");
  }, []);

  return (
    <PageWrapper>
      <style>{REFUND_CSS}</style>
      <DarkNav px={px} />

      {/* ── HERO: Green accent, reassuring ── */}
      <div style={{ position:"relative", overflow:"hidden", padding:`${isMobile?44:72}px ${px}px ${isMobile?32:52}px` }}>
        <div style={{
          position:"absolute", top:"-30%", right:"10%",
          width:500, height:500, borderRadius:"50%",
          background:"radial-gradient(circle,rgba(16,185,129,0.09) 0%,transparent 70%)",
          pointerEvents:"none",
        }} />

        <div style={{
          display:"inline-flex", alignItems:"center", gap:6, marginBottom:22,
          background:"rgba(16,185,129,0.1)", border:"1px solid rgba(16,185,129,0.3)",
          borderRadius:99, padding:"5px 16px",
          fontSize:11, fontWeight:700, color:"#10B981", letterSpacing:1.3, textTransform:"uppercase",
        }}>
          <CheckCircle size={12} /> Fair & Transparent
        </div>

        <h1 style={{
          fontSize:`clamp(26px,4.5vw,50px)`, fontWeight:900, margin:"0 0 14px", letterSpacing:-1.5, lineHeight:1.1,
          background:"linear-gradient(135deg,#fff 0%,#6EE7B7 70%,#34D399 100%)",
          WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
        }}>
          Return &amp; Refund Policy
        </h1>
        <p style={{ fontSize:14.5, color:"rgba(255,255,255,0.45)", margin:"0 0 8px", lineHeight:1.75, maxWidth:520 }}>
          We believe in transparent, fair refunds. Here's exactly what you can expect.
        </p>
        <p style={{ fontSize:12, color:"rgba(255,255,255,0.25)", margin:0 }}>
          Effective date: June 2026 · Last updated: July 2026
        </p>
      </div>

      {/* ── PROCESS FLOW: 5-step visual at top ── */}
      <div style={{ maxWidth:1100, margin:"0 auto", padding:`0 ${px}px 48px` }}>
        <div style={{
          background:"rgba(10,14,40,0.7)", backdropFilter:"blur(20px)",
          border:"1px solid rgba(16,185,129,0.2)", borderRadius:20,
          padding: isMobile?"20px 16px":"24px 36px",
          marginBottom:48,
        }}>
          <div style={{ fontSize:10, fontWeight:700, color:"rgba(52,211,153,0.6)", letterSpacing:1.5, textTransform:"uppercase", marginBottom:20, textAlign:"center" }}>
            How the refund process works
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:0, flexWrap: isMobile?"wrap":"nowrap" }}>
            {[
              { icon:<Send size={16}/>,      label:"Submit request", sub:"Email with payment ID",  color:"#34D399", step:1 },
              { icon:<AlertCircle size={16}/>,label:"We verify",      sub:"1–3 business days",     color:"#FBBF24", step:2 },
              { icon:<Clock size={16}/>,      label:"Decision",       sub:"Email notification",    color:"#60A5FA", step:3 },
              { icon:<CheckCircle size={16}/>,label:"Refund issued",  sub:"Via payment gateway",   color:"#10B981", step:4 },
              { icon:<CheckCircle size={16}/>,label:"Bank credit",    sub:"5–10 business days",    color:"#34D399", step:5 },
            ].map((s,i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", flex:1, minWidth: isMobile?"45%":0 }}>
                <div className={`refund-step-${i}`} style={{
                  flex:1, display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center",
                  padding:"0 8px", marginBottom: isMobile?16:0,
                }}>
                  <div style={{
                    width:44, height:44, borderRadius:12,
                    background:`rgba(52,211,153,0.1)`,
                    border:`1px solid rgba(52,211,153,0.25)`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    color:s.color, marginBottom:10,
                  }}>
                    {s.icon}
                  </div>
                  <div style={{ fontSize:12.5, fontWeight:700, color:"rgba(255,255,255,0.82)", marginBottom:3 }}>{s.label}</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)" }}>{s.sub}</div>
                </div>
                {i < 4 && !isMobile && (
                  <div style={{ flexShrink:0, width:24, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <div style={{ color:"rgba(52,211,153,0.35)", fontSize:16 }}>→</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div style={{ display:"flex", flexDirection:"column", gap:32 }}>

          {/* Overview */}
          <section id="overview" style={{ scrollMarginTop:82 }}>
            <h2 style={{ fontSize:18.5, fontWeight:700, color:"#fff", margin:"0 0 16px", letterSpacing:-0.3, paddingBottom:12, borderBottom:"1px solid rgba(16,185,129,0.2)" }}>Overview</h2>
            <P>This Return and Refund Policy applies to all purchases made on the <strong>LorryKhojo</strong> platform, operated by <strong>Gupta Vibhor and Associates</strong>, New Delhi, India. LorryKhojo provides a digital, subscription-free API credit service for FASTag-based vehicle tracking.</P>
            <P>Please read this policy carefully before making a purchase. By completing a payment on LorryKhojo, you acknowledge and agree to the terms described below.</P>
          </section>

          {/* Digital Goods */}
          <section id="digital" style={{ scrollMarginTop:82 }}>
            <h2 style={{ fontSize:18.5, fontWeight:700, color:"#fff", margin:"0 0 16px", letterSpacing:-0.3, paddingBottom:12, borderBottom:"1px solid rgba(16,185,129,0.2)" }}>Digital Goods Policy</h2>
            <SBox icon="ℹ️">LorryKhojo sells <strong>digital API credits</strong> — not physical goods. API credits are consumed on use and activated instantly after payment. Because of the nature of digital services, returns in the traditional sense do not apply.</SBox>
            <P>All plans (Starter, Standard, Growth, Pro) and top-up packs represent pre-purchased FASTag API call quotas. Once credits are activated on your account, they represent consumed or available digital service capacity, not a physical product that can be returned.</P>
          </section>

          {/* Eligible vs Non-Eligible side by side */}
          <div style={{ display:"grid", gridTemplateColumns: isMobile?"1fr":"1fr 1fr", gap:20 }}>
            {/* Eligible */}
            <section id="eligible" style={{ scrollMarginTop:82 }}>
              <div style={{
                background:"rgba(16,185,129,0.07)", border:"1px solid rgba(16,185,129,0.25)",
                borderRadius:18, padding:"22px 22px", height:"100%", boxSizing:"border-box",
              }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                  <CheckCircle size={18} color="#10B981" />
                  <h3 style={{ fontSize:15, fontWeight:800, color:"#fff", margin:0 }}>Eligible for Refund</h3>
                </div>
                <P>A full or partial refund <strong>may be considered</strong> in these circumstances:</P>
                <GreenBL items={["Purchase made within last 7 days AND no API calls made.","Technical error caused a double charge for the same order.","Credits were never added despite successful payment.","Payment was deducted but no order was created."]} />
                <SBox>If you were double-charged or credits weren't added, email us immediately at <a href="mailto:namaste@lorrykhojo.com" style={{color:"#34D399"}}>namaste@lorrykhojo.com</a> with your payment transaction ID.</SBox>
              </div>
            </section>

            {/* Non-eligible */}
            <section id="ineligible" style={{ scrollMarginTop:82 }}>
              <div style={{
                background:"rgba(248,113,113,0.05)", border:"1px solid rgba(248,113,113,0.2)",
                borderRadius:18, padding:"22px 22px", height:"100%", boxSizing:"border-box",
              }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                  <XCircle size={18} color="#F87171" />
                  <h3 style={{ fontSize:15, fontWeight:800, color:"#fff", margin:0 }}>Not Eligible</h3>
                </div>
                <P>The following are explicitly <strong>not eligible for refunds</strong>:</P>
                <RedBL items={["Any API calls that have already been used.","Free trial credits (2 calls on account creation).","Top-up packs where any calls have been used.","Plans purchased more than 7 days ago.","Partial refunds when some credits are used.","Coupon-discounted plan purchases.","Charges from user failure to manage account."]} />
              </div>
            </section>
          </div>

          {/* Process */}
          <section id="process" style={{ scrollMarginTop:82 }}>
            <h2 style={{ fontSize:18.5, fontWeight:700, color:"#fff", margin:"0 0 16px", letterSpacing:-0.3, paddingBottom:12, borderBottom:"1px solid rgba(16,185,129,0.2)" }}>Refund Process</h2>
            <P>To request a refund, email us at <a href="mailto:namaste@lorrykhojo.com" style={{color:"#34D399"}} onMouseEnter={e=>e.currentTarget.style.color="#fff"} onMouseLeave={e=>e.currentTarget.style.color="#34D399"}>namaste@lorrykhojo.com</a> with the subject line <strong>"Refund Request – [Your Registered Email]"</strong> and include:</P>
            <NeutralBL items={["Your registered email address on LorryKhojo","Payment Transaction ID (found in your payment confirmation email or the LorryKhojo dashboard)","Date of purchase","Reason for the refund request"]} />
            <P>Upon receiving your request, we will verify the eligibility of the refund against this policy, confirm the unused credit balance, and notify you of the outcome via email within <strong>5–7 business days</strong>.</P>
            <P>Approved refunds are processed back to the original payment instrument (card, UPI, net banking, or wallet) via our payment gateway. The time for the refunded amount to appear in your account depends on your bank or payment provider and is typically <strong>5–10 business days</strong> after our approval.</P>
          </section>

          {/* Timeline */}
          <section id="timeline" style={{ scrollMarginTop:82 }}>
            <h2 style={{ fontSize:18.5, fontWeight:700, color:"#fff", margin:"0 0 16px", letterSpacing:-0.3, paddingBottom:12, borderBottom:"1px solid rgba(16,185,129,0.2)" }}>Refund Timeline</h2>
            <GlassCard style={{ overflow:"hidden", border:"1px solid rgba(16,185,129,0.18)" }}>
              {[
                { step:"Day 1",    desc:"You submit refund request with payment ID and reason",                         dot:"#10B981" },
                { step:"Day 1–3",  desc:"We verify eligibility — check credits used, purchase date, payment status",   dot:"#FBBF24" },
                { step:"Day 3–5",  desc:"We notify you of approval or rejection via email",                             dot:"#60A5FA" },
                { step:"Day 5–15", desc:"If approved: refund initiated via payment gateway to original payment instrument",   dot:"#10B981" },
                { step:"Day 10–20",desc:"Refund reflects in your bank account (bank processing time varies)",           dot:"#34D399" },
              ].map((row,i) => (
                <div key={i} style={{
                  display:"flex", gap:16, padding:"14px 22px", alignItems:"flex-start",
                  background:i%2===0?"rgba(16,185,129,0.04)":"transparent",
                  borderBottom:i<4?"1px solid rgba(255,255,255,0.05)":"none",
                }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:80, flexShrink:0 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:row.dot, boxShadow:`0 0 8px ${row.dot}88`, flexShrink:0 }} />
                    <span style={{ fontSize:12, fontWeight:700, color:row.dot }}>{row.step}</span>
                  </div>
                  <span style={{ fontSize:13.5, color:"rgba(255,255,255,0.62)", lineHeight:1.7 }}>{row.desc}</span>
                </div>
              ))}
            </GlassCard>
          </section>

          {/* Disputes + Contact side by side */}
          <div style={{ display:"grid", gridTemplateColumns: isMobile?"1fr":"1fr 1fr", gap:20 }}>
            <section id="disputes" style={{ scrollMarginTop:82 }}>
              <h2 style={{ fontSize:18.5, fontWeight:700, color:"#fff", margin:"0 0 16px", letterSpacing:-0.3, paddingBottom:12, borderBottom:"1px solid rgba(16,185,129,0.2)" }}>Disputes</h2>
              <P>If your refund request is declined and you believe this is in error, you may escalate by replying to the refund decision email with additional documentation. We will review escalated cases within 7 additional business days.</P>
              <P>If you remain unsatisfied, you may file a complaint with the appropriate consumer forum under the Consumer Protection Act, 2019, or with your card-issuing bank for a chargeback review. However, we request that you first exhaust our internal resolution process before initiating a chargeback, as chargebacks may result in account suspension pending investigation.</P>
            </section>

            <section id="contact" style={{ scrollMarginTop:82 }}>
              <h2 style={{ fontSize:18.5, fontWeight:700, color:"#fff", margin:"0 0 16px", letterSpacing:-0.3, paddingBottom:12, borderBottom:"1px solid rgba(16,185,129,0.2)" }}>Contact</h2>
              <div style={{ background:"rgba(16,185,129,0.06)", border:"1px solid rgba(16,185,129,0.2)", borderRadius:14, padding:"18px 20px", display:"flex", flexDirection:"column", gap:12 }}>
                {[
                  {l:"Business",v:"Gupta Vibhor and Associates"},
                  {l:"Platform",v:"LorryKhojo"},
                  {l:"Address",v:"New Delhi, India"},
                ].map(r => (
                  <div key={r.l} style={{ display:"flex", gap:10, fontSize:13.5, paddingBottom:8, borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                    <span style={{ width:80, flexShrink:0, color:"rgba(52,211,153,0.6)", fontWeight:500 }}>{r.l}</span>
                    <span style={{ color:"rgba(255,255,255,0.78)", fontWeight:600 }}>{r.v}</span>
                  </div>
                ))}
                <div style={{ display:"flex", gap:10, fontSize:13.5 }}>
                  <span style={{ width:80, flexShrink:0, color:"rgba(52,211,153,0.6)", fontWeight:500 }}>Email</span>
                  <a href="mailto:namaste@lorrykhojo.com" style={{color:"#34D399",textDecoration:"none",fontWeight:600}} onMouseEnter={e=>e.currentTarget.style.color="#fff"} onMouseLeave={e=>e.currentTarget.style.color="#34D399"}>namaste@lorrykhojo.com</a>
                </div>
              </div>
            </section>
          </div>

          <SBox>Last updated: <strong>July 2026</strong> · LorryKhojo is a product of <strong>GVA Labs</strong>, operated by <strong>Gupta Vibhor and Associates</strong>, New Delhi, India.</SBox>
        </div>
      </div>

      <DarkFooter px={px} />
    </PageWrapper>
  );
}
